import React, { useState, useContext } from "react";
import { Modal, Button, SelectPicker } from "rsuite";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ConfiguracaoGlebaContext } from "../../../contexts/ConfiguracaoGlebaContext";
import {
  getProdutor,
  getFazenda,
  getGlebas,
  getSafras,
  getTipoSafra,
  getConfiguracaRelatorio,
} from "../../../Services/programation.service";

// Mapeia UMA cultura/cultivar do formato da API para o formato usado nas abas.
// Os insumos recebem id novo (key) e area nula (vão seguir a area da cultivar no destino).
const mapCultura = (cultura) => {
  const idBase = Date.now();
  const novoId = (i) => `copy-${idBase}-${i}`;
  return {
    area: null,
    culturaId: cultura?.cultura?.id,
    cultura: cultura?.cultura,
    cultivarId: cultura?.cultivar?.id,
    cultivar: cultura?.cultivar,
    propositoId: cultura?.proposito?.id,
    proposito: cultura?.proposito,
    tipoLancamentoId: cultura?.tipoLancamento?.id,
    quantidade: cultura?.quantidade,
    espaco: cultura?.espaco,
    pms: cultura?.pms,
    sacaPorHa: cultura?.sacaPorHa,
    sementePorSaca: cultura?.sementePorSaca,
    sementePorBag: cultura?.sementePorBag,
    kgHa: cultura?.kgHa,
    dataPlantio: cultura?.dataPlantio,
    observacoes: cultura?.observacoes,
    dataRealizacao: cultura?.dataRealizacao,
    sementePropria: cultura?.sementePropria,
    taxaVariavel: cultura?.taxaVariavel,
    ordem: cultura?.ordem,
    adubacao: (cultura?.adubacao || []).map((x, i) => ({
      ...x,
      id: novoId(`a${i}`),
      area: null,
      produtoAdubacaoId: x?.produtoAdubacao?.id,
      epocaAdubacaoId: x?.epocaAdubacao?.id,
      tipoAdubacaoId: x?.tipoAdubacao?.id,
    })),
    adubacaoFolhar: (cultura?.adubacaoFoliar || cultura?.adubacaoFolhar || []).map(
      (x, i) => ({
        ...x,
        id: novoId(`af${i}`),
        area: null,
        produtoAdubacaoFolharId: x?.produtoAdubacaoFolhar?.id,
      })
    ),
    herbicida: (cultura?.herbicida || []).map((x, i) => ({
      ...x,
      id: novoId(`h${i}`),
      area: null,
      produtoHerbicidaId: x?.produtoHerbicida?.id,
      tipoHerbicidaId: x?.tipoHerbicida?.id,
    })),
    inseticida: (cultura?.inseticida || []).map((x, i) => ({
      ...x,
      id: novoId(`i${i}`),
      area: null,
      produtoInseticidaId: x?.produtoInseticida?.id,
    })),
    fungicida: (cultura?.fungicida || []).map((x, i) => ({
      ...x,
      id: novoId(`f${i}`),
      area: null,
      produtoFungicidaId: x?.produtoFungicida?.id,
    })),
    outrosProdutos: (cultura?.outrosProdutos || []).map((x, i) => ({
      ...x,
      id: novoId(`o${i}`),
      area: null,
      produtoOutrosProdutosId: x?.produtoOutrosProdutos?.id,
      finalidadeOutrosProdutosId: x?.finalidadeOutrosProdutos?.id,
    })),
    tratamentoSementes: (cultura?.tratamentoSementes || []).map((x, i) => ({
      ...x,
      id: novoId(`t${i}`),
      area: null,
      produtoTratamentoId: x?.produtoTratamento?.id,
      classeTratamentoId: x?.classeTratamento?.id,
      tipoLancamentoTratamentoId: x?.tipoLancamentoTratamento?.id,
    })),
  };
};

const selectStyle = {
  width: "100%",
  border: "solid 1px rgba(1, 104, 32, 0.81)",
  borderRadius: "5px",
};

const CopiarCultivarModal = ({ open, onClose }) => {
  const {
    configuracaoGleba,
    setConfiguracaoGleba,
    setActiveCulturaIndex,
    setProgramacaoAlterada,
  } = useContext(ConfiguracaoGlebaContext);

  const [produtorId, setProdutorId] = useState(null);
  const [fazendaId, setFazendaId] = useState(null);
  const [glebaId, setGlebaId] = useState(null);
  // Safra e tipo já vêm preenchidos com o que está na tela (na prática se copia da mesma safra)
  const [safraId, setSafraId] = useState(configuracaoGleba?.safraId ?? null);
  const [tipoSafraId, setTipoSafraId] = useState(
    configuracaoGleba?.tipoSafraId ?? null
  );
  const [cultivarIndex, setCultivarIndex] = useState(null);

  // Ao ABRIR o modal, safra e tipo entram preenchidos com o que está na tela
  React.useEffect(() => {
    if (open) {
      setSafraId(configuracaoGleba?.safraId ?? null);
      setTipoSafraId(configuracaoGleba?.tipoSafraId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const optionsMap = (data, res) =>
    (res?.data || [])
      .filter((x) => !x.excluido)
      .map((x) => ({ label: x.nome, value: x.id }));

  const produtoresQuery = useQuery(["copiaProdutores"], async () => {
    const { res } = await getProdutor();
    return optionsMap(null, res);
  });
  const fazendasQuery = useQuery(
    ["copiaFazendas", produtorId],
    async () => {
      if (!produtorId) return [];
      const { res } = await getFazenda(produtorId);
      return optionsMap(null, res);
    },
    { enabled: !!produtorId }
  );
  const glebasQuery = useQuery(
    ["copiaGlebas", fazendaId],
    async () => {
      if (!fazendaId) return [];
      const { res } = await getGlebas(fazendaId);
      return optionsMap(null, res);
    },
    { enabled: !!fazendaId }
  );
  const safrasQuery = useQuery(["copiaSafras"], async () => {
    const { res } = await getSafras();
    return optionsMap(null, res);
  });
  const tiposQuery = useQuery(["copiaTipos"], async () => {
    const { res } = await getTipoSafra();
    return optionsMap(null, res);
  });

  const podeBuscar = glebaId && safraId && tipoSafraId;
  const configQuery = useQuery(
    ["copiaConfig", glebaId, safraId, tipoSafraId],
    async () => {
      const { res } = await getConfiguracaRelatorio({
        glebaId,
        safraId,
        tipoSafraId,
      });
      return res?.data || null;
    },
    { enabled: !!podeBuscar }
  );

  const culturasOrigem = configQuery?.data?.culturas || [];
  const opcoesCultivar = culturasOrigem.map((c, i) => ({
    label: `${c?.cultura?.nome || "?"}${
      c?.cultivar?.nome ? " · " + c.cultivar.nome : ""
    }`,
    value: i,
  }));

  const resetar = () => {
    setProdutorId(null);
    setFazendaId(null);
    setGlebaId(null);
    setSafraId(null);
    setTipoSafraId(null);
    setCultivarIndex(null);
  };

  const handleCopiar = () => {
    if (cultivarIndex == null || !culturasOrigem[cultivarIndex]) {
      toast.warning("Escolha qual cultivar copiar.");
      return;
    }
    const blocoBase = mapCultura(culturasOrigem[cultivarIndex]);

    // Pode copiar até do PRÓPRIO talhão (pra reaproveitar os insumos e só trocar
    // a cultivar). Não bloqueia aqui: se ficar cultivar repetida, o Salvar avisa.
    const existentesAgora = configuracaoGleba?.culturas || [];

    // Índice da nova aba calculado FORA do updater (dentro pode rodar depois e ativar a aba errada)
    const novoIndex = existentesAgora.length;

    // Calcula a area restante usando o estado MAIS ATUAL (prev), evitando valor velho.
    setConfiguracaoGleba((prev) => {
      const existentes = prev?.culturas || [];
      const glebaArea = Number(
        prev?.defaultValues?.area ?? prev?.gleba?.area ?? 0
      );
      const usado = existentes.reduce((s, c) => s + (Number(c?.area) || 0), 0);
      const restante = Number((glebaArea - usado).toFixed(3));
      // A cultivar copiada entra com a ÁREA QUE FALTA no talhão — nunca estoura.
      // Se o talhão já está cheio, entra sem área pra você liberar espaço/ajustar.
      const novoBloco = {
        ...blocoBase,
        area: restante > 0 ? restante : null,
      };
      // A observação agora é POR CULTIVAR (vem no novoBloco via mapCultura).
      return { ...prev, culturas: [...existentes, novoBloco] };
    });

    setActiveCulturaIndex(novoIndex);
    setProgramacaoAlterada(true);
    toast.success("Cultivar copiada! Confira a área e salve.");
    resetar();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      backdrop="static"
      className="header-modal"
    >
      <Modal.Header>
        <Modal.Title>Copiar cultivar de outro talhão</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ marginBottom: 12, color: "#555" }}>
          Escolha o talhão de origem e a cultivar. Ela entra como uma nova aba
          aqui, já com o manejo — sem apagar o que você já tem.
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: "1 1 30%", minWidth: 170 }}>
            Produtor
            <SelectPicker
              block
              data={produtoresQuery.data || []}
              value={produtorId}
              loading={produtoresQuery.isFetching}
              onChange={(v) => {
                setProdutorId(v);
                setFazendaId(null);
                setGlebaId(null);
              }}
              style={selectStyle}
            />
          </div>
          <div style={{ flex: "1 1 30%", minWidth: 170 }}>
            Fazenda
            <SelectPicker
              block
              data={fazendasQuery.data || []}
              value={fazendaId}
              disabled={!produtorId}
              loading={fazendasQuery.isFetching}
              onChange={(v) => {
                setFazendaId(v);
                setGlebaId(null);
              }}
              style={selectStyle}
            />
          </div>
          <div style={{ flex: "1 1 30%", minWidth: 170 }}>
            Talhão
            <SelectPicker
              block
              data={glebasQuery.data || []}
              value={glebaId}
              disabled={!fazendaId}
              loading={glebasQuery.isFetching}
              onChange={setGlebaId}
              style={selectStyle}
            />
          </div>
          <div style={{ flex: "1 1 30%", minWidth: 170 }}>
            Safra
            <SelectPicker
              block
              data={safrasQuery.data || []}
              value={safraId}
              onChange={setSafraId}
              style={selectStyle}
            />
          </div>
          <div style={{ flex: "1 1 30%", minWidth: 170 }}>
            Tipo de safra
            <SelectPicker
              block
              data={tiposQuery.data || []}
              value={tipoSafraId}
              onChange={setTipoSafraId}
              style={selectStyle}
            />
          </div>
        </div>

        {podeBuscar && (
          <div style={{ marginTop: 18 }}>
            {configQuery.isFetching ? (
              <p>Buscando programação...</p>
            ) : opcoesCultivar.length === 0 ? (
              <p style={{ color: "#b3261e" }}>
                Esse talhão/safra não tem programação pra copiar.
              </p>
            ) : (
              <div>
                Qual cultivar copiar?
                <SelectPicker
                  block
                  data={opcoesCultivar}
                  value={cultivarIndex}
                  onChange={setCultivarIndex}
                  style={selectStyle}
                />
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} appearance="ghost">
          Cancelar
        </Button>
        <span
          title={
            cultivarIndex == null
              ? "Escolha o talhão de origem e a cultivar primeiro"
              : undefined
          }
        >
          <Button
            onClick={handleCopiar}
            appearance="primary"
            disabled={cultivarIndex == null}
            style={{ background: "rgba(1, 104, 32, 0.81)", width: 160 }}
          >
            Copiar cultivar
          </Button>
        </span>
      </Modal.Footer>
    </Modal>
  );
};

export default CopiarCultivarModal;
